// React
import { useContext } from "react"

// Translation
import { useTrans, Trans } from "@/i18n"

// UI
import { CardBody, CardWrapper } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { FormGroup } from "@/components/form-controls/FormGroup"
import { Button } from "@/components/Button"
import { Input, Label } from "@/components/form-controls"

// Icons
import { FiChevronRight } from "@/lib/icons"

// Context
import { RedirectContext } from "@/context/redirectContext"
import { useCurrentUserMangoPayStatus } from "@/context/user"

// Query
import { useUserMangoPayEnrollmentUrlQuery } from "@/api/graphql"

// Security
import { rewriteMangoPayReturnUrlToCurrentUrl } from "@/lib/security"

/**
 * MangoPaySCA
 * @returns
 */
export const MangoPaySCABanner = () => {
	// Context
	const mangoPayStatus = useCurrentUserMangoPayStatus()

	// i18n
	const t = useTrans("investor")

	return (
		<CardWrapper className="relative mb-4">
			<CardBody>
				<Heading as="h2" styleAs="h5" className="mb-3 sm:truncate">
					{t("investor:investor.identity.copy.mangopay.title")}
				</Heading>
				<FormGroup
					heading={
						<>
							<p className="whitespace-break-spaces">
								<Trans
									ns="investor"
									i18nKey="investor:investor.identity.copy.mangopay.copy"
								/>
							</p>

							{/** Show text if mangopay still needs to be validated */}
							{mangoPayStatus === false ? (
								<p className="whitespace-break-spaces">
									<Trans
										ns="investor"
										i18nKey="investor:investor.identity.copy.mangopay.copy.redirect"
									/>
								</p>
							) : null}

							{/** Logo */}
							<img
								src="/assets/partners/mangopay_logo.png"
								alt="Mangopay logo"
								className="w-1/2"
							/>

							{/** FAQ */}
							<p className="whitespace-break-spaces">
								<Trans
									i18nKey="investor:investor.identity.copy.mangopay.copy.faq"
									components={{
										/* eslint-disable jsx-a11y/anchor-has-content */
										a: (
											<a
												className="font-medium text-secondary-300 hover:text-secondary-700"
												href="https://informatie.zonhub.com/support/solutions/articles/17000147317-hoe-mangopay-als-payment-service-provider-zonhub-helpt"
												target="_blank"
												rel="noreferrer"
											/>
											/* eslint-enable jsx-a11y/anchor-has-content */
										),
									}}
								/>
							</p>
						</>
					}
				>
					<div>
						<div className="flex flex-row gap-2">
							<div className="flex-1">
								<Label>
									{t(
										"investor:investor.identity.copy.mangopay.status.label",
									)}
								</Label>
								<Input
									type="text"
									required
									className="mt-1 block w-full"
									readOnly
									disabled
									value={
										mangoPayStatus
											? t(
													"investor:investor.identity.copy.mangopay.status.verified",
												)
											: t(
													"investor:investor.identity.copy.mangopay.status.unverified",
												)
									}
								/>
							</div>

							{/** Show if user active is FALSE or NULL */}
							{!mangoPayStatus ? (
								<MangopayEnrollmentButton />
							) : null}
						</div>
					</div>
				</FormGroup>
			</CardBody>
		</CardWrapper>
	)
}

/**
 * MangopayEnrollmentButton
 * @returns
 */
const MangopayEnrollmentButton = () => {
	// i18n
	const t = useTrans("investor")

	// Query
	const { data } = useUserMangoPayEnrollmentUrlQuery()

	// Context
	const { redirect } = useContext(RedirectContext)

	return (
		<Button
			className="self-end"
			onClick={() => {
				redirect(
					rewriteMangoPayReturnUrlToCurrentUrl(
						data?.me?.mangopay_user_remote?.enrollment_url ?? "",
					),
				)
			}}
			disabled={!data?.me?.mangopay_user_remote?.enrollment_url}
		>
			{t("profile:profile.contact_details.confirm")}
			<FiChevronRight className="ml-2" />
		</Button>
	)
}
